import { useAsyncData, useRoute } from '#imports'
import { ref } from 'vue'

import { useRelevanceAlgo } from '@backmarket/nuxt-layer-reviews/composables/useRelevanceAlgo'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'

import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import {
  reviewsConfig,
  reviewsRequestEndpoints,
} from '~/scopes/reviews/reviews-display/constants'
import { transformReviewsResponse } from '~/scopes/reviews/reviews-display/utils/transformReviewsResponse'

import {
  BACKBOX_GRADE_LABELS,
  type BackboxGradeLabel,
  MIN_RATE,
  PAGE_SIZE,
} from './useFetchReviewsPerGrade.constants'
import type { ReviewsPerGrade } from './useFetchReviewsPerGrade.types'

function useFetchReviewsPerGrade() {
  const route = useRoute()
  const locale = useI18nLocale()
  const { productId } = useUrlParams()
  const $httpFetch = useHttpFetch()

  const uuid: string = route.params?.uuid ? route.params.uuid : productId
  const routeName = route.name?.toString() ?? ''
  const endpoint = reviewsRequestEndpoints?.[routeName]?.getReviews

  if (!endpoint || !uuid) {
    return {
      data: ref<ReviewsPerGrade | null>(null),
      error: ref(),
      pending: ref(false),
      execute: () => Promise.resolve(),
    }
  }

  return useAsyncData(
    getAsyncDataKey('reviews-per-grade', {
      uuid,
      locale,
      routeName,
    }),
    async () => {
      const currentRouteName = route.name?.toString() ?? ''
      const headers = reviewsConfig?.[currentRouteName]?.headers
      const timeout = reviewsConfig?.[currentRouteName]?.timeout
      const { orderBy, isNewRelevanceAlgoEnabled } = useRelevanceAlgo()

      const fetchReviewsForGrade = async (gradeLabel: BackboxGradeLabel) => {
        const data = await $httpFetch(endpoint, {
          timeout,
          headers,
          pathParams: { uuid },
          query: {
            order_by: orderBy,
            global_rate__gte: MIN_RATE,
            page_size: PAGE_SIZE,
            with_attachments: true,
            translation_locale: locale,
            backbox_grade_label__in: gradeLabel,
            ...(isNewRelevanceAlgoEnabled
              ? { has_high_quality_product_attachment: true }
              : {}),
          },
        })

        return [
          gradeLabel,
          transformReviewsResponse(data?.results ?? []),
        ] as const
      }

      const entries = await Promise.all(
        BACKBOX_GRADE_LABELS.map(fetchReviewsForGrade),
      )

      const grades: ReviewsPerGrade = {}

      for (const [gradeLabel, reviews] of entries) {
        if (reviews.length > 0) {
          grades[gradeLabel] = reviews
        }
      }

      return grades
    },
    {
      server: false,
      lazy: true,
    },
  )
}

export function useReviewsPerGrade() {
  const { data, pending } = useFetchReviewsPerGrade()

  return {
    reviewsPerGrade: data,
    isPendingReviewsPerGrade: pending,
  }
}
